/**
 * How a duel ended, from one seat.
 *
 * Read off the two aircraft's own statuses rather than the encounter's
 * `EncounterStatus`, which is friendly-vs-hostile and therefore says
 * "enemyDown" to *both* peers when the guest goes down. Same fight, two
 * seats, two correct-but-opposite verdicts — and the engine has no notion of
 * which seat is asking, nor should it.
 */

import type { EncounterState } from "@red-opus/engine";

export const DUEL_OUTCOMES = ["flying", "victory", "defeat", "draw", "desync", "disconnected"] as const;

export type DuelOutcome = (typeof DUEL_OUTCOMES)[number];

/** Whether the duel has stopped being flyable, for whatever reason. */
export function isDuelDecided(outcome: DuelOutcome): boolean {
  return outcome !== "flying";
}

function destroyed(state: EncounterState, id: string): boolean {
  return state.aircraft.find((entry) => entry.id === id)?.status === "destroyed";
}

/** The verdict from `localId`'s cockpit. Only ever `flying`/`victory`/`defeat`/`draw`. */
export function duelOutcomeOf(state: EncounterState, localId: string, remoteId: string): DuelOutcome {
  const me = destroyed(state, localId);
  const them = destroyed(state, remoteId);
  if (me && them) return "draw";
  if (me) return "defeat";
  if (them) return "victory";
  return "flying";
}

/**
 * `ENEMY DESTROYED` and `YOU'VE BEEN SHOT DOWN` are the original's own wording
 * (recovered in `combat-hud.ts`'s `OUTCOME_BANNERS`); the other three are
 * port-side lines for conditions the 1990 game had no concept of.
 */
export const DUEL_BANNERS: Readonly<Record<DuelOutcome, string>> = {
  flying: "",
  victory: "ENEMY DESTROYED",
  defeat: "YOU'VE BEEN SHOT DOWN",
  draw: "YOU WENT DOWN TOGETHER",
  desync: "DUEL DESYNCHRONISED",
  disconnected: "CONNECTION LOST",
};

/** The line under the banner: what happened and what can be done about it. */
export const DUEL_EXPLANATIONS: Readonly<Record<DuelOutcome, string>> = {
  flying: "",
  victory: "He went down on both screens — the two simulations agreed the whole way.",
  defeat: "He got you. Rematch keeps the room; a new seed puts you back at the merge.",
  draw: "You took each other with you.",
  desync:
    "The two simulations stopped producing the same fight, so the result would not have meant anything. " +
    "This port ends the duel rather than let the two of you fly different matches; v1 has no resync.",
  disconnected: "Your opponent's connection went away.",
};

export function duelBanner(outcome: DuelOutcome): string | undefined {
  return outcome === "flying" ? undefined : DUEL_BANNERS[outcome];
}

/** A rematch is only offered for a duel that ended by being *flown* to a finish. */
export function canRematch(outcome: DuelOutcome): boolean {
  return outcome === "victory" || outcome === "defeat" || outcome === "draw";
}
